<template>
  <teleport to="body">
    <div class="modal" v-if="isVisible">
      <div class="modal-content">
        <span class="close" @click="closeModal">&times;</span>
        <div class="text-container">
          <h2>{{ post.title }}</h2>
          <div class="post-meta">
            <p class="post-date">{{ post.date }}</p>
            
            <!-- Regular share button for non-Safari browsers -->
            <a 
              v-if="!isSafari"
              href="#" 
              class="share-button"
              @click.prevent="sharePost">
              share
            </a>
            
            <!-- Special Safari-only share element -->
            <div v-if="isSafari" class="safari-share-wrapper">
              <div 
                class="safari-share" 
                @click="sharePost">
                share
              </div>
            </div>
          </div>
          <div v-html="formattedContent" class="blog-content"></div>
        </div>
        <div class="modal-footer">
          <span class="reading-time">↓&nbsp;&nbsp;&nbsp;{{ post.readingTime }}</span>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
export default {
  props: {
    post: Object,
    isVisible: Boolean,
  },
  emits: ['close', 'share'],
  
  data() {
    return {
      isSafari: false
    };
  },
  
  mounted() {
    // Detect Safari browser - more specific detection
    this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) || 
                   /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    // For Safari, add the share element directly to the DOM after mounting
    if (this.isSafari && this.isVisible) {
      this.$nextTick(() => {
        this.ensureSafariShareVisible();
      });
    }
  },
  
  updated() {
    // Ensure Safari share is visible after updates
    if (this.isSafari && this.isVisible) {
      this.$nextTick(() => {
        this.ensureSafariShareVisible();
      });
    }
  },
  
  computed: {
    formattedContent() {
      if (!this.post) return "";
      let content = this.post.content;
      
      // Replace quotes
      content = content.replace(/\[quote\]([\s\S]*?)\[\/quote\]/g, '<blockquote class="blog-quote">$1</blockquote>');
      
      // Replace links
      content = content.replace(/\[link:(.*?)\]/g, (match, title) => 
        `<span class="blog-link" @click="openBlogPostByTitle('${title.trim()}')">${title.trim()}</span>`
      );
      
      // Process line breaks - using a more efficient approach
      content = content.split("\n").map(line => line.trim()).join("<br>");
      
      // Process paragraphs
      const paragraphs = content.split("<br><br>").map((para, index) => {
        const cleanPara = para.replace(/\[image\]/g, "");
        return index === 0 ? 
          `<p class="first-paragraph">${cleanPara}</p>` : 
          `<p>${cleanPara}</p>`;
      });
      
      // Add image if present
      const image = this.post.image ? 
        `<img src="${this.post.image}" alt="Blog Image" class="blog-image" />` : 
        "";
      
      // Insert image after first paragraph
      if (paragraphs.length > 1) {
        paragraphs.splice(1, 0, image);
      } else {
        paragraphs.push(image);
      }
      
      // Add red line at the end of the content
      paragraphs.push('<hr class="blog-end-line">');
      
      return paragraphs.join("");
    },
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },
    openBlogPostByTitle(title) {
      this.$emit('open-blog', title);
    },
    sharePost() {
      this.$emit('share', this.post);
    },
    ensureSafariShareVisible() {
      const wrapper = document.querySelector('.safari-share-wrapper');
      if (wrapper) {
        // Force visibility through direct DOM manipulation
        wrapper.style.display = 'block';
        wrapper.style.visibility = 'visible';
        wrapper.style.opacity = '1';
        
        const shareEl = wrapper.querySelector('.safari-share');
        if (shareEl) {
          shareEl.style.display = 'block';
          shareEl.style.visibility = 'visible';
          shareEl.style.opacity = '1';
        }
      }
    }
  },
}
</script>

<style>
/* Base modal styling */
.modal {
  display: flex;
  position: fixed;
  z-index: 9999;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
  background-color: rgba(255, 255, 255, 0.9);
  justify-content: center;
  align-items: center;
  font-family: 'Castoro', serif;
}

.modal-content {
  position: relative;
  background-color: white;
  padding: 50px;
  border-radius: 5px;
  margin: 5dvh;
  border: 1px solid #ccc;
  max-width: 85dvw;
  min-height: 80dvh;
  padding-bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  text-rendering: optimizeLegibility;
}

.text-container {
  max-width: 90%;
  max-height: 85%;
  margin: 0;
  margin-top: -50px !important;
  padding: 20px;
  text-align: left;
  line-height: 1.6;
  flex-grow: 1;
  max-height: none;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
  padding-bottom: 10px;
  min-height: 0;
}

/* Title styling - base size */
.text-container h2,
.modal-content .text-container h2,
.modal .text-container h2 {
  font-size: 1.8em;
  line-height: 1.4;
  margin-bottom: 0.5em;
  font-weight: normal;
}

/* Blog content styling */
.blog-content {
  overflow-y: auto;
  flex: 1 1 auto;
  margin-bottom: 8px !important;
}

.blog-image {
  display: block;
  max-width: 100%;
  max-height: 300px;
  width: auto;
  height: auto;
  padding-bottom: 10px;
  object-fit: cover;
  object-position: top;
}

.blog-content .first-paragraph::first-letter {
  max-width: 60%;
  font-size: 3.2em;
  font-weight: bold;
  float: left;
  margin-right: 8px;
  line-height: 0.8;
  padding-top: 10px;
  text-transform: uppercase;
}

.close {
  position: absolute;
  top: 5%;
  right: 5%;
  color: #e72100;
  font-size: 28px;
  font-weight: bold;
  cursor: pointer;
  z-index: 100;
}

.close:hover,
.close:focus {
  color: #333;
  text-decoration: none;
  cursor: pointer;
}

/* Reading time styling */
.reading-time {
  font-size: 0.8em !important;
  color: #e72100 !important;
  font-style: italic !important;
  display: inline-block !important;
}

/* Blog quote styling */
.blog-quote {
  font-style: italic;
  border-left: 3px solid #e72100;
  padding-left: 15px;
  margin: 20px 0;
  color: #666;
}

/* Link styling */
.blog-link {
  color: #e72100;
  cursor: pointer;
  text-decoration: underline;
}

.blog-link:hover {
  color: #333;
}

/* Red line at the end of blog content */
.blog-end-line {
  border: none;
  height: 1px;
  background-color: #e72100;
  margin: 15px 0 65px 0;
  width: 100%;
}

/* CONSOLIDATED MEDIA QUERIES */

/* Tablet styles (768px and below) */
@media screen and (min-width: 480px) and (max-width: 768px) {
  .modal-content {
    max-width: 90dvw;
    padding: 30px;
    height: 85dvh;
  }
  
  .text-container {
    max-width: 95%;
    max-height: 92%;
    padding-bottom: 30px;
    margin-top: -30px !important;
  }
  
  .text-container h2,
  .modal-content .text-container h2,
  .modal .text-container h2 {
    font-size: 1.2em !important;
    line-height: 1.3 !important;
  }
  
  .reading-time {
    font-size: 0.75em;
  }
}

/* Mobile styles (480px and below) */
@media screen and (max-width: 480px) {
  .modal-content {
    max-width: 95dvw;
    max-height: 95dvh;
    margin: 10px;
    padding: 30px;
  }
  
  .text-container {
    height: 95% !important;
    padding-bottom: 30px;
    min-width: 100%;
    margin-top: -20px !important;
  }
  
  .close {
    top: 10px;
    right: 15px;
    font-size: 24px;
  }
  
  .text-container h2,
  .modal-content .text-container h2,
  .modal .text-container h2 {
    font-size: 1em !important;
    line-height: 1.2 !important;
  }
  
  .reading-time {
    font-size: 0.7em !important;
    padding-bottom: 10px;
  }
  
  .blog-content .first-paragraph::first-letter {
    font-size: 3em !important;
    padding-top: 10px;
  }
}

/* Small height screens */
@media screen and (max-height: 600px) {
  .modal-content {
    max-height: 85dvh !important;
    padding: 25px;
  }
  
  .text-container {
    max-height: 80dvh !important;
    margin-top: -15px !important;
  }
}

/* Landscape orientation on mobile */
@media (max-height: 400px) and (orientation: landscape) {
  .modal-content {
    max-height: 80dvh !important;
    padding: 15px;
    height: 80dvh;
  }
  
  .text-container {
    min-height: 85dvh !important;
    margin-top: -10px !important;
  }
  
  .modal-content h2 {
    font-size: 1em !important;
    line-height: 1.21 !important;
    margin-top: 0 !important;
    margin-bottom: 0px !important;
    font-weight: bold !important;
  }
  
  .modal-content .post-date {
    font-size: 0.8em !important;
    margin-bottom: 0px !important;
  }
  
  .reading-time {
    font-size: 0.7em !important;
  }
  
  .blog-content .first-paragraph::first-letter {
    font-size: 1.5em !important;
    padding-top: 6px !important;
  }
  
  .close {
    top: 10px !important;
    right: 15px !important;
    font-size: 20px !important;
  }
}

/* Add this to your style section */
.modal-footer {
  width: 100%;
  padding-bottom: 15px;
  text-align: center;
  margin-top: auto;
  background-color: white;
}

/* Update modal content to accommodate footer */
.modal-content {
  display: flex;
  flex-direction: column;
  padding-bottom: 0;
  max-height: 85dvh;
  height: 85dvh;
}


/* Adjust for tablet */
@media screen and (min-width: 480px) and (max-width: 768px) {
  .modal-footer {
    margin-top: -15px;
  }
}
/* Adjust for mobile */
@media screen and (max-width: 480px) {
  .modal-footer {
    margin-top: -25px;
    padding-bottom: 8px;
  }
  
  .reading-time {
    font-size: 0.7em !important;
  }
}

/* Adjust for landscape */
@media (max-height: 400px) and (orientation: landscape) {
  .modal-footer {
    padding: 5px 0;
  }

  /* Adjust for landscape */
@media (max-height: 400px) and (orientation: landscape) {
  .safari-share, .share-button {
    margin-top: 5px;
    margin-right: 10px;
  }
}

}

/* Completely redesigned styles for maximum compatibility */
.post-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
  width: 100%;
}

.post-date {
  margin: 0;
  padding: 0;
}

/* Share button styling to match collapse-toggle button */
.share-button, 
.safari-share {
  background: none;
  border: 1px solid #e72100;
  color: #e72100;
  cursor: pointer;
  font-family: 'Castoro', serif;
  font-style: italic;
  transition: all 0.3s ease;
  font-size: 0.9em;
  top: 10px;
  padding-left: 20px;
  padding-right: 20px;
  display: inline-block;
  text-decoration: none;
}

.share-button:hover,
.safari-share:hover {
  background: #e72100;
  color: white;
  text-decoration: none;
}

/* Ensure Safari share is always visible */
.safari-share-wrapper {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
}

@media (max-width: 480px) {
  .share-button, 
  .safari-share {
    padding-left: 20px;
    padding-right: 20px;
    font-size: 0.7em;
    margin-top: -5px;
  }
}

.blog-content p {
  text-align: justify; /* Apply justification by default */
}

/* Remove justification on mobile */
@media screen and (max-width: 480px) {
  .blog-content p {
    text-align: left; /* Reset to left alignment on mobile */
  }
}

</style>